import { useCallback } from 'react'
import { useDispatch, useSelector } from 'store'
import * as TableUserAction from 'slicers/TableSlicers/TableUserSlice'
import * as ModalUserAction from 'slicers/ModalUserSlice'
import * as ModalAction from 'slicers/ModalSlice'
import * as PageAction from 'slicers/PageSlice'
import { postAPI, response } from 'utils/apiUtil'
import { buttonInfo, voidF } from 'types/FunctionTypes'
import { commonMsg } from 'assets/MessageCode'
import { responseUser, requestUser, getRequestUserMail, requestUserMail, requestUserDelete, getRequestUserDelete } from 'utils/requestUtil'
import { keyInfo } from 'types/ReferTypes'
import { select } from 'types/SearchTypes'

// サマリチェックリスト //================================//
type check = (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.MouseEvent<HTMLTableRowElement, MouseEvent>, isChecked: boolean) => void
export const useCheckList = (user: responseUser[]): [requestUser[], check] => {
  const dispatch = useDispatch()
  const selectList = useSelector((state) => state.TableUserReducer.selectList)
  const allClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.MouseEvent<HTMLTableRowElement, MouseEvent>, isChecked: boolean) => {
      e.preventDefault()
      dispatch(TableUserAction.checkUserAll({ user: user, isChecked: isChecked }))
    },
    [user],
  )
  return [selectList, allClick]
}

/**
 * ==================================================================================
 * 選択処理
 * @param param0
 * @returns
 * ==================================================================================
 */
export const useCheck = (user: responseUser, isChecked: boolean): [voidF] => {
  const dispatch = useDispatch()
  const loading = useSelector((state) => state.PageReducer.loading)

  // チェック処理 //================================//
  const check = useCallback(() => {
    if (!loading) {
      dispatch(PageAction.deleteMessage())
      dispatch(TableUserAction.checkUser({ user: user, isChecked: isChecked }))
    }
  }, [user, isChecked, loading])
  return [check]
}

/**
 * ボタン押下処理(メール再送・追加・削除)
 * @returns
 */
export const useButtons = (refetch: voidF): buttonInfo[] => {
  const dispatch = useDispatch()
  const loading = useSelector((state) => state.PageReducer.loading)
  const isSuper = useSelector((state) => state.LoginReducer.isSuper)
  const selectList = useSelector((state) => state.TableUserReducer.selectList)
  const ADD_MAIL = useSelector((state) => state.LoginReducer.MAIL)

  const changeErrMsg = () => dispatch(PageAction.changeMessageError(commonMsg.ERROR_MSG)) //
  const loaded = () => dispatch(PageAction.endLoading()) //
  const reflectResult = (r: response<any>) => {
    dispatch(PageAction.changeMessage(r))
    if (!r.error) {
      // 検索後メッセージを残すためにフラグを更新
      dispatch(PageAction.changeIsNotDeleteMessage(true))
      refetch()
    }
  }

  // 登録メール再送ボタン押下処理 //================================//
  const clickSendMail = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault()
      if (!loading) {
        // ローディング開始
        dispatch(PageAction.startLoading('処理中...'))
        await postAPI<requestUserMail[], {}>('/postUserSendMail', getRequestUserMail(selectList), reflectResult, changeErrMsg, loaded)
      }
    },
    [loading, selectList],
  )

  // 追加ボタン押下処理 //================================//
  const clickAdd = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    if (!loading) {
      dispatch(PageAction.format())
      dispatch(ModalUserAction.format())
      dispatch(ModalAction.open())
    }
  }

  // 削除ボタン押下処理 //================================//
  const clickDelete = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    if (!loading) {
      if (window.confirm('選択されたユーザーを削除します。よろしいですか？')) {
        // ローディング開始
        dispatch(PageAction.startLoading('処理中...'))
        await postAPI<requestUserDelete[], {}>('/postUserDelete', getRequestUserDelete(selectList, ADD_MAIL), reflectResult, changeErrMsg, loaded)
      }
    }
  }

  const isChecked = selectList.length > 0

  if (isSuper) {
    return [
      {
        disabled: !isChecked,
        label: '登録ﾒｰﾙ再送',
        click: clickSendMail,
      },
      {
        disabled: !isSuper,
        label: '追加',
        click: clickAdd,
      },
      {
        disabled: !isSuper || !isChecked,
        label: '削除',
        click: clickDelete,
        alert: true,
      },
    ]
  }
  return [
    // スーパーユーザー以外はメール再送のみ
    {
      disabled: !isChecked,
      label: '登録ﾒｰﾙ再送',
      click: clickSendMail,
    },
  ]
}

/**
 * ユーザー追加サブ画面表示処理
 * @returns
 */
export const useUserAdd = (): voidF => {
  const dispatch = useDispatch()
  const isJapell = useSelector((state) => state.LoginReducer.isJapell)
  const keyInfo = useSelector((state) => state.LoginReducer.keyInfo)
  const loading = useSelector((state) => state.PageReducer.loading)

  // ユーザー追加表示処理 //================================//
  const clickAdd = async () => {
    if (!loading) {
      if (!isJapell) {
        dispatch(ModalAction.startLoading('処理中...'))
        await postAPI<{ keyInfo: keyInfo[] }, { tokuiList: select[]; tenList: select[] }>(
          '/postUserAddList',
          { keyInfo: keyInfo },
          (r) => {
            dispatch(ModalUserAction.changeTokuiList(r.body.tokuiList))
            dispatch(ModalUserAction.changeTenList(r.body.tenList))
          },
          () => dispatch(ModalAction.changeMessageError(commonMsg.ERROR_MSG)),
          () => dispatch(ModalAction.endLoading()),
        )
      }
      dispatch(ModalAction.open())
    }
  }

  return clickAdd
}

// 検索後フォーマット処理 //================================//
export const useSearchedFormat = (): [responseUser[], voidF] => {
  const dispatch = useDispatch()
  const selectList = useSelector((state) => state.TableUserReducer.selectList)
  const format = () => {
    dispatch(TableUserAction.formatCheck())
  }
  return [selectList, format]
}
